import React, { useEffect, useState } from "react";
import { usePhrase } from "@/contexts/LanguageContext";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";

export default function OpportunityDetail() {
  const pt = usePhrase();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    indicative_amount_min: 1000000, indicative_amount_max: 2000000,
    indicative_rate_min: 10.5, indicative_rate_max: 12.5, rate_type: "Floating",
    indicative_tenure_months: 60, indicative_moratorium_months: 3,
    processing_fee_pct: 1, collateral_expectation: "As per policy",
    personal_guarantee: true, cgtmse_consideration: "May be considered",
    additional_documents: ["Latest ITR", "Bank statement"], branch: "Bhopal Main",
    expected_response_days: 7, offer_validity_days: 15, notes_to_borrower: "",
  });
  const [declineReason, setDeclineReason] = useState("Ticket size");

  useEffect(() => { api.get(`/lender/opportunities/${id}`).then((r) => setData(r.data)); }, [id]);

  const express = async () => {
    try { await api.post(`/lender/opportunities/${id}/interest`, form); toast.success(pt("Interest posted")); nav("/bank/opportunities"); } catch (e) { toast.error(pt("Failed")); }
  };
  const decline = async () => {
    try { await api.post(`/lender/opportunities/${id}/decline`, { reason: declineReason }); toast.success(pt("Declined")); nav("/bank/opportunities"); } catch (e) { toast.error(pt("Failed")); }
  };

  if (!data) return <div className="text-slate-500">{pt("Loading…")}</div>;
  const b = data.business_anonymized || data.business?.business_identity || {};

  return (
    <div className="space-y-6 max-w-5xl" data-testid="opp-detail">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 brand-heading">{pt("Opportunity detail")}</h1>
        <p className="text-sm text-slate-600 mt-1">
          {data.consent_granted
            ? pt("Borrower has granted consent — full credit file visible below.")
            : pt("Anonymized view. Full business identity is revealed only after borrower consents.")}
        </p>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading">{data.consent_granted ? pt("Business") : pt("Anonymized profile")}</h2>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <div><div className="text-slate-500 text-xs">{pt("District")}</div><div className="font-semibold">{b.district || "—"}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Sector")}</div><div className="font-semibold">{pt(b.industry || data.business_anonymized?.sector) || "—"}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Constitution")}</div><div className="font-semibold">{pt(b.constitution) || "—"}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Vintage")}</div><div className="font-semibold">{b.vintage_years || "—"} {pt("yr")}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("MSME category")}</div><div className="font-semibold">{pt(b.msme_category) || "—"}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Women-owned")}</div><div className="font-semibold">{b.women_owned ? pt("Yes") : pt("No")}</div></div>
          {data.consent_granted && data.business && (
            <>
              <div><div className="text-slate-500 text-xs">{pt("Legal name")}</div><div className="font-semibold">{data.business.business_identity?.legal_business_name}</div></div>
              <div><div className="text-slate-500 text-xs">{pt("PAN")}</div><div className="font-mono">{data.business.business_identity?.pan}</div></div>
              <div><div className="text-slate-500 text-xs">{pt("Udyam")}</div><div className="font-mono">{data.business.business_identity?.udyam_number}</div></div>
            </>
          )}
        </div>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading">{pt("Requirement")}</h2>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <div><div className="text-slate-500 text-xs">{pt("Requested")}</div><div className="font-semibold">{formatINR(data.requirement?.requested_amount)}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Purpose")}</div><div className="font-semibold">{pt(data.requirement?.purpose)}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Collateral")}</div><div className="font-semibold">{data.requirement?.collateral_available ? pt("Available") : pt("None")}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Project cost")}</div><div className="font-semibold">{formatINR(data.requirement?.project_cost)}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Promoter contribution")}</div><div className="font-semibold">{formatINR(data.requirement?.promoter_contribution)}</div></div>
          <div><div className="text-slate-500 text-xs">{pt("Tenure preference")}</div><div className="font-semibold">{data.requirement?.preferred_tenure_months} {pt("mo")}</div></div>
        </div>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading">{pt("Match rationale")}</h2>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700 mb-1">{pt("Matched conditions")}</div>
            <ul className="space-y-1">{(data.match?.matched_conditions || []).map((c) => <li key={c}>✓ {pt(c)}</li>)}</ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-orange-700 mb-1">{pt("Unmatched conditions")}</div>
            <ul className="space-y-1">{(data.match?.unmatched_conditions || []).map((c) => <li key={c}>! {pt(c)}</li>)}</ul>
          </div>
        </div>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading">{pt("Express indicative interest")}</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {[
            ["Amount min (₹)", "indicative_amount_min", "number"], ["Amount max (₹)", "indicative_amount_max", "number"],
            ["Rate min (%)", "indicative_rate_min", "number"], ["Rate max (%)", "indicative_rate_max", "number"],
            ["Tenure (mo)", "indicative_tenure_months", "number"], ["Moratorium (mo)", "indicative_moratorium_months", "number"],
            ["Processing fee (%)", "processing_fee_pct", "number"], ["Branch", "branch", "text"],
            ["Expected response (days)", "expected_response_days", "number"], ["Offer validity (days)", "offer_validity_days", "number"],
          ].map(([l, k, t]) => (
            <div key={k}>
              <label className="block text-xs text-slate-500">{pt(l)}</label>
              <input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: t === "number" ? Number(e.target.value) : e.target.value })} data-testid={`interest-${k}`} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5" />
            </div>
          ))}
          <div>
            <label className="block text-xs text-slate-500">{pt("CGTMSE")}</label>
            <select value={form.cgtmse_consideration} onChange={(e) => setForm({ ...form, cgtmse_consideration: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5">
              {["May be considered", "Not available for this product", "Requires further appraisal"].map((o) => <option key={o} value={o}>{pt(o)}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs text-slate-500">{pt("Notes to borrower")}</label>
            <textarea value={form.notes_to_borrower} onChange={(e) => setForm({ ...form, notes_to_borrower: e.target.value })} data-testid="interest-notes" className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5" rows={2} />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={express} data-testid="express-interest-btn" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 text-sm font-semibold">{pt("Express interest")}</button>
          <select value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
            {["Ticket size","Geography","Sector","Vintage","Turnover","Existing leverage","Documentation","Product mismatch","Policy mismatch","Other"].map((o) => <option key={o} value={o}>{pt(o)}</option>)}
          </select>
          <button onClick={decline} data-testid="decline-opp-btn" className="rounded-md border border-slate-300 px-4 py-2 text-sm">{pt("Decline")}</button>
        </div>
        <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          {pt("This is an expression of interest based on limited information. It is not a sanction, approval or binding commitment. Final terms are subject to the lender's appraisal, policy, verification and documentation.")}
        </div>
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}
